import { RestaurantPurchasePaymentCondition } from '../../../types/restaurant/RestaurantPurchaseOrderTypes';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const parseDate = (value: string) => {
  if (!DATE_PATTERN.test(value)) return null;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? date
    : null;
};

const formatDate = (date: Date) =>
  [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');

const daysInMonth = (year: number, month: number) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const addCalendarMonths = (date: Date, amount: number) => {
  const targetMonth = date.getUTCMonth() + amount;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;

  return new Date(
    Date.UTC(
      targetYear,
      normalizedMonth,
      Math.min(date.getUTCDate(), daysInMonth(targetYear, normalizedMonth))
    )
  );
};

export const calculateRestaurantPurchaseDueDate = (
  purchaseDate: string,
  termCode: string | null | undefined,
  amount: number | null | undefined
): string | null => {
  const start = parseDate(purchaseDate);
  const safeAmount = Number(amount);

  if (!start || !Number.isInteger(safeAmount) || safeAmount <= 0) return null;

  if (termCode === '01') {
    start.setUTCDate(start.getUTCDate() + safeAmount);
    return formatDate(start);
  }

  if (termCode === '02') {
    return formatDate(addCalendarMonths(start, safeAmount));
  }

  if (termCode === '03') {
    return formatDate(addCalendarMonths(start, safeAmount * 12));
  }

  return null;
};

export const calculateRestaurantPurchaseTermAmount = (
  purchaseDate: string,
  dueDate: string,
  termCode: string | null | undefined
): number | null => {
  const start = parseDate(purchaseDate);
  const due = parseDate(dueDate);

  if (!start || !due || due <= start) return null;

  if (termCode === '01') {
    return Math.round((due.getTime() - start.getTime()) / 86_400_000);
  }

  const months =
    (due.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    due.getUTCMonth() -
    start.getUTCMonth();
  const completedMonths =
    addCalendarMonths(start, months) > due ? months - 1 : months;

  if (termCode === '02') {
    return Math.max(1, completedMonths);
  }

  if (termCode === '03') {
    return Math.max(1, Math.floor(completedMonths / 12));
  }

  return null;
};

export const withRestaurantPurchaseTermAmount = (
  condition: RestaurantPurchasePaymentCondition,
  amount: number | null
): RestaurantPurchasePaymentCondition => ({
  ...condition,
  creditDays: condition.termCode === '01' ? amount : null,
  creditMonths: condition.termCode === '02' ? amount : null,
  creditYears: condition.termCode === '03' ? amount : null,
});

export const getRestaurantPurchasePaymentError = (
  purchaseDate: string,
  condition: RestaurantPurchasePaymentCondition | null | undefined
): string | null => {
  if (!condition) return 'Seleccione una condición de pago.';
  if (condition.code !== 'CREDITO') return null;

  if (!condition.termId || !condition.termCode) {
    return 'Seleccione la unidad del plazo.';
  }

  const amount =
    condition.termCode === '01'
      ? condition.creditDays
      : condition.termCode === '02'
      ? condition.creditMonths
      : condition.termCode === '03'
      ? condition.creditYears
      : null;

  if (amount == null || Number(amount) <= 0) {
    return 'El plazo debe ser mayor que cero.';
  }

  if (!condition.creditDueDate) {
    return 'Indique la fecha de vencimiento.';
  }

  if (purchaseDate && condition.creditDueDate < purchaseDate) {
    return 'La fecha de vencimiento debe ser igual o posterior a la compra.';
  }

  return null;
};

export const isRestaurantPurchasePaymentComplete = (
  purchaseDate: string,
  condition: RestaurantPurchasePaymentCondition | null | undefined
) => getRestaurantPurchasePaymentError(purchaseDate, condition) === null;
